import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Assuming AuthGuard and EmailGuard are defined in ../App and work correctly
import { AuthGuard, EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FaWater, FaTrashAlt, FaPen, FaPlus, FaArrowLeft, FaTools } from 'react-icons/fa'; // Import FaTools for Service
import { StandardURL } from "../App";

// --- Reusable Modal Component ---
const Modal = ({ title, isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

        {/* Modal content */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-xl leading-6 font-bold text-gray-900 mb-4 border-b pb-2" id="modal-title">
              {title}
            </h3>
            {children}
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---
const BillsPropertyPage = () => {
  const [waterBill, setWaterBill] = useState(null);
  const [trashBill, setTrashBill] = useState(null);
  const [serviceBill, setServiceBill] = useState(null); // NEW: Service Bill state
  const [token, setToken] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [rentalId, setRentalId] = useState("");
  const [isAddWaterBillModalOpen, setIsAddWaterBillModalOpen] = useState(false);
  const [previousUnits, setPreviousUnits] = useState("");
  const [currentUnits, setCurrentUnits] = useState("");
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedWaterBill, setSelectedWaterBill] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Global loading for all actions
  const [isPageLoading, setIsPageLoading] = useState(true); // For initial fetch
  const [isAddTrashBillModalOpen, setIsAddTrashBillModalOpen] = useState(false);
  
  const [serviceAmount, setServiceAmount] = useState(""); // NEW: Service Amount state
  const [isAddServiceBillModalOpen, setIsAddServiceBillModalOpen] = useState(false); // NEW: Service Modal state

  const navigate = useNavigate();

  // Unified fetch function
  const fetchBills = async (t, pId, rId) => {
    try {
      // 1. Fetch Water Bill Data
      const waterBillResponse = await axios.get(
        `${StandardURL}/iNyumba/admin/waterbill/getspecificbyrental/${rId}`,
        { headers: { Authorization: `Bearer ${t}` } }
      );
      const fetchedWaterBill = waterBillResponse.data.response;
      setWaterBill(fetchedWaterBill);

      if (fetchedWaterBill && fetchedWaterBill.length > 0) {
        localStorage.setItem("updatedWaterBillId", fetchedWaterBill[0].waterbill_id);
      } else {
          localStorage.removeItem("updatedWaterBillId");
      }

      // 2. Fetch Trash Bill Data
      const trashBillResponse = await axios.get(
        `${StandardURL}/iNyumba/admin/trashbill/getspecificbyrental/${rId}`,
        { headers: { Authorization: `Bearer ${t}` } }
      );
      setTrashBill(trashBillResponse.data.response);
      
      // 3. NEW: Fetch Service Bill Data
      const serviceBillResponse = await axios.get(
        `${StandardURL}/iNyumba/admin/servicepropertybill/getspecificbyrental/${rId}`,
        { headers: { Authorization: `Bearer ${t}` } }
      );
      const fetchedServiceBill = serviceBillResponse.data.response;
      setServiceBill(fetchedServiceBill);

      if (fetchedServiceBill && fetchedServiceBill.length > 0) {
        localStorage.setItem("selectedservicebillid", fetchedServiceBill[0].servicepropertybill_id);
      } else {
        localStorage.removeItem("selectedservicebillid");
      }

    } catch (error) {
      console.error("Error fetching bills:", error);
      toast.error("Failed to fetch bill records.");
    }
  };

  // Initial Data Fetch
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedPropertyId = localStorage.getItem("selectedPropertyId");
    const storedRentalId = localStorage.getItem("selectedRentalId");

    if (storedToken && storedPropertyId && storedRentalId) {
      setToken(storedToken);
      setPropertyId(storedPropertyId);
      setRentalId(storedRentalId);
      fetchBills(storedToken, storedPropertyId, storedRentalId).finally(() => setIsPageLoading(false));
    } else {
      toast.error("Required context (Token, Property ID, Rental ID) is missing.");
      setIsPageLoading(false);
    }
  }, []);

  // --- Water Bill Handlers ---

  const handleUpdateWaterBill = (billId) => {
    const selectedBill = waterBill.find((item) => item.waterbill_id === billId);

    if (selectedBill) {
      // Set previous units to the current units of the *latest* bill, as per original logic
      setSelectedWaterBill({
        previous_units: selectedBill.currentunits,
        current_units: "",
      });
      setIsUpdateModalOpen(true);
    } else {
      toast.error("Selected bill data not found.");
    }
  };

  const handleUpdateWaterBillSubmit = async () => {
    if (parseFloat(selectedWaterBill.current_units) <= parseFloat(selectedWaterBill.previous_units)) {
        toast.error("Current units must be greater than the previous units.");
        return;
    }
    
    setIsLoading(true);
    const WaterBillId = localStorage.getItem("updatedWaterBillId"); // Use the ID from localStorage
    const requestData = {
      previous_units: selectedWaterBill.previous_units,
      current_units: selectedWaterBill.current_units,
    };

    try {
      await axios.patch(
        `${StandardURL}/iNyumba/admin/waterbill/update/${WaterBillId}/${propertyId}`,
        requestData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      await fetchBills(token, propertyId, rentalId); // Re-fetch data to update UI
      toast.success("Water bill updated successfully.");
      setIsUpdateModalOpen(false);
    } catch (error) {
      const errorMessage = error.response?.data?.response || "Failed to update water bill.";
      toast.error(errorMessage);
      console.error("Error updating water bill:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddWaterBill = async () => {
    if (!previousUnits || !currentUnits) {
      toast.error("Both Previous and Current units are required.");
      return;
    }
    if (parseFloat(currentUnits) <= parseFloat(previousUnits)) {
        toast.error("Current units must be greater than the previous units.");
        return;
    }

    setIsLoading(true);
    const requestData = {
      previous_units: previousUnits,
      current_units: currentUnits,
    };

    try {
      await axios.post(
        `${StandardURL}/iNyumba/admin/waterbill/create/${propertyId}/${rentalId}`,
        requestData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      
      await fetchBills(token, propertyId, rentalId); // Re-fetch data
      toast.success("Water bill added successfully.");
      setIsAddWaterBillModalOpen(false);
      setPreviousUnits("");
      setCurrentUnits("");
    } catch (error) {
      const errorMessage = error.response?.data?.response || "Failed to add water bill.";
      toast.error(errorMessage);
      console.error("Error adding water bill:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Trash Bill Handlers ---

  const handleAddTrashBill = async () => {
    setIsLoading(true);
    // Original logic sends an empty body for the POST request
    try {
      await axios.post(
        `${StandardURL}/iNyumba/admin/trashbill/create/${propertyId}/${rentalId}`,
        {}, 
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      
      await fetchBills(token, propertyId, rentalId); // Re-fetch data
      toast.success("Trash bill added successfully.");
      setIsAddTrashBillModalOpen(false);
    } catch (error) {
      const errorMessage = error.response?.data?.response || "Failed to add trash bill.";
      toast.error(errorMessage);
      console.error("Error adding trash bill:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTrashBill = async (billId) => {
    setIsLoading(true);
    // Original logic uses a PATCH with an empty body
    try {
      await axios.patch(
        `${StandardURL}/iNyumba/admin/trashbill/update/${billId}/${propertyId}`,
        {},
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      await fetchBills(token, propertyId, rentalId); // Re-fetch data
      toast.success("Trash Bill updated successfully.");
    } catch (error) {
      const errorMessage = error.response?.data?.response || "Failed to update trash bill.";
      toast.error(errorMessage);
      console.error("Error updating trash bill:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- NEW: Service Bill Handler (Combined Add/Update) ---
  const handleServiceBillSubmit = async () => {
    if (!serviceAmount || isNaN(parseFloat(serviceAmount)) || parseFloat(serviceAmount) <= 0) {
      toast.error("A valid Service Amount (must be greater than 0) is required.");
      return;
    }

    setIsLoading(true);
    const isUpdate = serviceBill && serviceBill.length > 0;
    const amountValue = parseFloat(serviceAmount);
    // Use the ID from local storage which was set during fetch
    const billId = localStorage.getItem("selectedservicebillid"); 
    const requestData = { amount: amountValue };

    try {
      if (isUpdate) {
        // PATCH: Update existing service bill
        await axios.patch(
          `${StandardURL}/iNyumba/admin/servicepropertybill/update/${billId}/${propertyId}`,
          requestData,
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        toast.success("Service Bill updated successfully.");
      } else {
        // POST: Create new service bill
        await axios.post(
          `${StandardURL}/iNyumba/admin/servicepropertybill/create/${propertyId}/${rentalId}`,
          requestData,
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        toast.success("Service Bill added successfully.");
      }

      await fetchBills(token, propertyId, rentalId);
      setIsAddServiceBillModalOpen(false);
      setServiceAmount("");
    } catch (error) {
      const errorMessage = error.response?.data?.response || `Failed to ${isUpdate ? 'update' : 'add'} service bill.`;
      toast.error(errorMessage);
      console.error("Error handling service bill:", error);
    } finally {
      setIsLoading(false);
    }
  };


  // --- Final Bill Posting Handler ---
  const handleCreateBill = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/admin/bill/create-bill/${propertyId}/${rentalId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.response);
      navigate(-1); // Navigate back after successful bill creation
    } catch (error) {
      const errorMessage = error.response?.data?.response || "Failed to post bill to user.";
      toast.error(errorMessage);
      console.error("Error creating bill:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Component Rendering ---

  if (isPageLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <Oval color="#4A90E2" secondaryColor="#74B9FF" height={50} width={50} ariaLabel="loading-spinner" />
      </div>
    );
  }

  const latestWaterBill = waterBill && waterBill.length > 0 ? waterBill[0] : null;
  const latestTrashBill = trashBill && trashBill.length > 0 ? trashBill[0] : null;
  const latestServiceBill = serviceBill && serviceBill.length > 0 ? serviceBill[0] : null; // NEW: Latest Service Bill

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              Property Bills Management
            </h1>

            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-blue-600 hover:text-blue-800 font-semibold mb-6 transition duration-150"
            >
              <FaArrowLeft className="mr-2" /> Go Back
            </button>
            
            {/* Water Bill Section */}
            <div className="bg-white shadow-lg rounded-xl p-6 mb-8 border-t-4 border-blue-600">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                  <FaWater className="mr-2 text-blue-500" /> Water Bill
                </h2>
                {!latestWaterBill && (
                  <button
                    onClick={() => setIsAddWaterBillModalOpen(true)}
                    disabled={isLoading}
                    className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg shadow-md transition duration-150 ease-in-out ${
                      isLoading ? "bg-gray-400 text-white cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                  >
                    <FaPlus className="mr-2" /> Add Water Bill
                  </button>
                )}
              </div>

              {latestWaterBill ? (
                <div className="space-y-3">
                  <p className="text-lg text-gray-700">
                    <strong className="font-medium text-gray-900">Previous Units:</strong> {latestWaterBill.previousunits}
                  </p>
                  <p className="text-lg text-gray-700">
                    <strong className="font-medium text-gray-900">Current Units:</strong> {latestWaterBill.currentunits}
                  </p>
                  <p className="text-md text-gray-500 italic pt-2">
                    Latest reading recorded on: {new Date(latestWaterBill.created_at).toLocaleDateString()}
                  </p>
                  <button
                    onClick={() => handleUpdateWaterBill(latestWaterBill.waterbill_id)}
                    disabled={isLoading}
                    className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg shadow-md transition duration-150 ease-in-out ${
                      isLoading ? "bg-gray-400 text-white cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600 text-white"
                    } mt-4`}
                  >
                    <FaPen className="mr-2" /> Update Latest Reading
                  </button>
                </div>
              ) : (
                <div className="bg-blue-50 p-4 rounded-lg text-blue-800 border border-blue-200">
                  No Water Bill record found for this rental unit. Please add the initial bill.
                </div>
              )}
            </div>
            
            {/* Trash Bill Section */}
            <div className="bg-white shadow-lg rounded-xl p-6 mb-8 border-t-4 border-green-600">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                  <FaTrashAlt className="mr-2 text-green-500" /> Trash Bill
                </h2>
                <button
                  onClick={() => {
                    // Decide whether to Add (if no bill) or Update (if bill exists)
                    if (latestTrashBill) {
                      handleUpdateTrashBill(latestTrashBill.trashbillid);
                    } else {
                      setIsAddTrashBillModalOpen(true);
                    }
                  }}
                  disabled={isLoading}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg shadow-md transition duration-150 ease-in-out ${
                    isLoading ? "bg-gray-400 text-white cursor-not-allowed" : (latestTrashBill ? "bg-green-500 hover:bg-green-600 text-white" : "bg-green-600 hover:bg-green-700 text-white")
                  }`}
                >
                  {latestTrashBill ? <><FaPen className="mr-2" /> Update Latest Amount</> : <><FaPlus className="mr-2" /> Add Trash Bill</>}
                </button>
              </div>

              {latestTrashBill ? (
                <div className="space-y-3">
                  <p className="text-lg text-gray-700">
                    <strong className="font-medium text-gray-900">Amount:</strong> KES {latestTrashBill.amount ? parseFloat(latestTrashBill.amount).toFixed(2) : '0.00'}
                  </p>
                  <p className="text-md text-gray-500 italic pt-2">
                    Latest amount recorded on: {new Date(latestTrashBill.created_at).toLocaleDateString()}
                  </p>
                </div>
              ) : (
                <div className="bg-red-50 p-4 rounded-lg text-red-800 border border-red-200">
                  No Trash Bill record found for this rental unit. Please add the initial bill.
                </div>
              )}
            </div>

            {/* NEW: Service Bill Section */}
            <div className="bg-white shadow-lg rounded-xl p-6 mb-8 border-t-4 border-yellow-600">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                  <FaTools className="mr-2 text-yellow-600" /> Service Bill
                </h2>
                <button
                  onClick={() => {
                    // Set current service amount for pre-filling or clear for new bill
                    setServiceAmount(latestServiceBill ? latestServiceBill.amount : "");
                    setIsAddServiceBillModalOpen(true);
                  }}
                  disabled={isLoading}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg shadow-md transition duration-150 ease-in-out ${
                    isLoading ? "bg-gray-400 text-white cursor-not-allowed" : (latestServiceBill ? "bg-yellow-500 hover:bg-yellow-600 text-white" : "bg-yellow-600 hover:bg-yellow-700 text-white")
                  }`}
                >
                  {latestServiceBill ? <><FaPen className="mr-2" /> Update Latest Amount</> : <><FaPlus className="mr-2" /> Add Service Bill</>}
                </button>
              </div>

              {latestServiceBill ? (
                <div className="space-y-3">
                  <p className="text-lg text-gray-700">
                    <strong className="font-medium text-gray-900">Amount:</strong> KES {latestServiceBill.amount ? parseFloat(latestServiceBill.amount).toFixed(2) : '0.00'}
                  </p>
                  <p className="text-md text-gray-500 italic pt-2">
                    Latest amount recorded on: {new Date(latestServiceBill.created_at).toLocaleDateString()}
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 p-4 rounded-lg text-yellow-800 border border-yellow-200">
                  No Service Bill record found for this rental unit. Please add the initial bill.
                </div>
              )}
            </div>
            
            {/* Final Bill Posting Action */}
            <div className="bg-white shadow-lg rounded-xl p-6 border-t-4 border-purple-600 text-center">
              <p className="mb-4 text-gray-700 font-medium">
                Once **all three** bills are up-to-date, click "Post Bill to User" to generate the final monthly statement.
              </p>
               <button
                  onClick={handleCreateBill}
                  disabled={isLoading || !latestWaterBill || !latestTrashBill || !latestServiceBill} // Require all three bills
                  className={`w-full max-w-sm mx-auto flex items-center justify-center px-6 py-3 border border-transparent text-base font-bold rounded-lg shadow-xl transition-colors duration-300 ease-in-out 
                    ${(isLoading || !latestWaterBill || !latestTrashBill || !latestServiceBill)
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-purple-600 hover:bg-purple-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      }`}
                >
                  {isLoading ? (
                      <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="white" />
                  ) : (
                      "Post Bill to User"
                  )}
                </button>
                {(!latestWaterBill || !latestTrashBill || !latestServiceBill) && (
                    <p className="mt-2 text-sm text-red-500">
                        * All three bills (Water, Trash, and Service) must exist before posting the final bill.
                    </p>
                )}
            </div>
            
          </div>
        </div>
        
        {/* --- MODALS --- */}
        
        {/* Add Water Bill Modal (No change) */}
        <Modal 
          title="Add New Water Bill" 
          isOpen={isAddWaterBillModalOpen} 
          onClose={() => setIsAddWaterBillModalOpen(false)}
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="previousUnits" className="block text-sm font-medium text-gray-700">Previous Units:</label>
              <input
                type="number"
                id="previousUnits"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                value={previousUnits}
                onChange={(e) => setPreviousUnits(e.target.value)}
                placeholder="Enter previous meter reading"
              />
            </div>
            <div>
              <label htmlFor="currentUnits" className="block text-sm font-medium text-gray-700">Current Units:</label>
              <input
                type="number"
                id="currentUnits"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                value={currentUnits}
                onChange={(e) => setCurrentUnits(e.target.value)}
                placeholder="Enter current meter reading"
              />
            </div>
            <button
              onClick={handleAddWaterBill}
              disabled={isLoading}
              className={`w-full flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:text-sm ${
                isLoading ? "bg-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              }`}
            >
              {isLoading ? <Oval color="#fff" height={20} width={20} /> : "Submit Water Bill"}
            </button>
          </div>
        </Modal>
        
        {/* Update Water Bill Modal (No change) */}
        {isUpdateModalOpen && selectedWaterBill && (
          <Modal 
            title="Update Water Bill Reading" 
            isOpen={isUpdateModalOpen} 
            onClose={() => setIsUpdateModalOpen(false)}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Previous Units:</label>
                <input
                  type="text"
                  className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2 bg-gray-100 cursor-not-allowed"
                  value={selectedWaterBill.previous_units}
                  readOnly
                />
              </div>
              <div>
                <label htmlFor="updateCurrentUnits" className="block text-sm font-medium text-gray-700">New Current Units:</label>
                <input
                  type="number"
                  id="updateCurrentUnits"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                  value={selectedWaterBill.current_units}
                  onChange={(e) => setSelectedWaterBill({ ...selectedWaterBill, current_units: e.target.value })}
                  placeholder="Enter the new meter reading"
                />
              </div>
              <button
                onClick={handleUpdateWaterBillSubmit}
                disabled={isLoading}
                className={`w-full flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:text-sm ${
                  isLoading ? "bg-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
              >
                {isLoading ? <Oval color="#fff" height={20} width={20} /> : "Update Reading"}
              </button>
            </div>
          </Modal>
        )}
        
        {/* Add Trash Bill Modal (No change) */}
        <Modal 
          title="Add New Trash Bill" 
          isOpen={isAddTrashBillModalOpen} 
          onClose={() => setIsAddTrashBillModalOpen(false)}
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              By confirming, you will be creating a new default Trash Bill entry for the current billing cycle.
            </p>
            <button
              onClick={handleAddTrashBill}
              disabled={isLoading}
              className={`w-full flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:text-sm ${
                isLoading ? "bg-gray-500 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              }`}
            >
              {isLoading ? <Oval color="#fff" height={20} width={20} /> : "Confirm Add Trash Bill"}
            </button>
          </div>
        </Modal>

        {/* NEW: Service Bill Modal */}
        <Modal 
          title={latestServiceBill ? "Update Service Bill Amount" : "Add New Service Bill"} 
          isOpen={isAddServiceBillModalOpen} 
          onClose={() => setIsAddServiceBillModalOpen(false)}
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              {latestServiceBill ? `Current amount: KES ${parseFloat(latestServiceBill.amount).toFixed(2)}` : "Enter the amount for the new Service Bill."}
            </p>
            <div>
              <label htmlFor="serviceAmount" className="block text-sm font-medium text-gray-700">Service Amount (KES):</label>
              <input
                type="number"
                id="serviceAmount"
                className="mt-1 focus:ring-yellow-500 focus:border-yellow-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                value={serviceAmount}
                onChange={(e) => setServiceAmount(e.target.value)}
                placeholder="Enter amount"
                min="0"
              />
            </div>
            <button
              onClick={handleServiceBillSubmit}
              disabled={isLoading}
              className={`w-full flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:text-sm ${
                isLoading ? "bg-gray-500 cursor-not-allowed" : "bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              }`}
            >
              {isLoading ? <Oval color="#fff" height={20} width={20} /> : (latestServiceBill ? "Update Service Bill" : "Submit Service Bill")}
            </button>
          </div>
        </Modal>

      </AuthGuard>
    </EmailGuard>
  );
};

export default BillsPropertyPage;